/**
 * DOS518 - Digital Operations System for Wildfire Management
 * Copyright (c) 2024-2025 Angelo Irtilaç — All rights reserved.
 *
 * tileCacheManager.ts
 * Scarica e cachea tile satellite Esri per uso offline.
 * Usa IndexedDB separato dal MediaDB esistente.
 */

const TILE_DB_NAME = 'DOS518_TileCache';
const TILE_DB_VERSION = 1;
const TILE_STORE = 'tiles';

// URL per ogni layer — usati da precacheTiles e da CachedTileLayer in BaseTileLayer
export const TILE_LAYER_URLS: Record<string, string> = {
  satellite:   'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
  street:      'https://a.tile.openstreetmap.org/{z}/{x}/{y}.png',
  topo:        'https://server.arcgisonline.com/ArcGIS/rest/services/World_Topo_Map/MapServer/tile/{z}/{y}/{x}',
  opentopomap: 'https://tile.opentopomap.org/{z}/{x}/{y}.png',
};

export interface TileCacheProgress {
  total: number;
  downloaded: number;
  failed: number;
  percent: number;
  done: boolean;
}

// Conversione lat/lon → tile x,y per dato zoom
function latLonToTile(lat: number, lon: number, zoom: number) {
  const n = Math.pow(2, zoom);
  const x = Math.floor((lon + 180) / 360 * n);
  const latRad = lat * Math.PI / 180;
  const y = Math.floor((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2 * n);
  return { x, y };
}

// Genera tutte le tile necessarie per un'area (bbox) a un dato zoom
function getTilesForArea(
  lat: number, lon: number,
  radiusKm: number,
  zoom: number
): { z: number; x: number; y: number }[] {
  // Approssimazione: 1 grado lat ≈ 111km
  const deltaLat = radiusKm / 111;
  const deltaLon = radiusKm / (111 * Math.cos(lat * Math.PI / 180));

  const topLeft     = latLonToTile(lat + deltaLat, lon - deltaLon, zoom);
  const bottomRight = latLonToTile(lat - deltaLat, lon + deltaLon, zoom);

  const tiles: { z: number; x: number; y: number }[] = [];
  for (let x = topLeft.x; x <= bottomRight.x; x++) {
    for (let y = topLeft.y; y <= bottomRight.y; y++) {
      tiles.push({ z: zoom, x, y });
    }
  }
  return tiles;
}

// Apre (o crea) il database tile
async function openTileDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(TILE_DB_NAME, TILE_DB_VERSION);
    req.onupgradeneeded = () => {
      req.result.createObjectStore(TILE_STORE, { keyPath: 'key' });
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror   = () => reject(req.error);
  });
}

// Salva una tile nel DB (privata)
async function saveTile(db: IDBDatabase, key: string, blob: Blob, areaId?: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(TILE_STORE, 'readwrite');
    tx.objectStore(TILE_STORE).put({ key, blob, ts: Date.now(), areaId });
    tx.oncomplete = () => resolve();
    tx.onerror    = () => reject(tx.error);
  });
}

// Salva una tile (pubblica — usata dal caching passivo in BaseTileLayer)
export async function saveTileCached(key: string, blob: Blob, areaId?: string): Promise<void> {
  const db = await openTileDB();
  return saveTile(db, key, blob, areaId);
}

// Legge una tile dal DB (usato da Leaflet custom layer)
export async function getTileCached(key: string): Promise<Blob | null> {
  const db = await openTileDB();
  return new Promise((resolve) => {
    const tx  = db.transaction(TILE_STORE, 'readonly');
    const req = tx.objectStore(TILE_STORE).get(key);
    req.onsuccess = () => resolve(req.result?.blob ?? null);
    req.onerror   = () => resolve(null);
  });
}

// Conta le tile in cache
export async function countCachedTiles(): Promise<number> {
  const db = await openTileDB();
  return new Promise((resolve) => {
    const tx  = db.transaction(TILE_STORE, 'readonly');
    const req = tx.objectStore(TILE_STORE).count();
    req.onsuccess = () => resolve(req.result);
    req.onerror   = () => resolve(0);
  });
}

// Svuota la cache tile
export async function clearTileCache(): Promise<void> {
  const db = await openTileDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(TILE_STORE, 'readwrite');
    tx.objectStore(TILE_STORE).clear();
    tx.oncomplete = () => resolve();
    tx.onerror    = () => reject(tx.error);
  });
}

// Calcola dimensione totale cache in MB
export async function getCacheSizeMB(): Promise<number> {
  const db = await openTileDB();
  return new Promise((resolve) => {
    const tx  = db.transaction(TILE_STORE, 'readonly');
    const req = tx.objectStore(TILE_STORE).getAll();
    req.onsuccess = () => {
      const records: { blob: Blob }[] = req.result || [];
      const totalBytes = records.reduce((sum, r) => sum + (r.blob?.size || 0), 0);
      resolve(Math.round(totalBytes / 1024 / 1024 * 10) / 10); // 1 decimale
    };
    req.onerror = () => resolve(0);
  });
}

// Informazioni complete sulla cache
export async function getCacheInfo(): Promise<{ tiles: number; sizeMB: number; areas: string[] }> {
  const db = await openTileDB();
  return new Promise((resolve) => {
    const tx  = db.transaction(TILE_STORE, 'readonly');
    const req = tx.objectStore(TILE_STORE).getAll();
    req.onsuccess = () => {
      const records: { key: string; blob: Blob; areaId?: string }[] = req.result || [];
      const totalBytes = records.reduce((sum, r) => sum + (r.blob?.size || 0), 0);
      const areas = [...new Set(records.map(r => r.areaId).filter(Boolean))] as string[];
      resolve({
        tiles: records.length,
        sizeMB: Math.round(totalBytes / 1024 / 1024 * 10) / 10,
        areas,
      });
    };
    req.onerror = () => resolve({ tiles: 0, sizeMB: 0, areas: [] });
  });
}

// Svuota cache per una specifica area (identificata da areaId)
export async function clearTileCacheForArea(areaId: string): Promise<number> {
  const db = await openTileDB();
  return new Promise((resolve, reject) => {
    const tx      = db.transaction(TILE_STORE, 'readwrite');
    const store   = tx.objectStore(TILE_STORE);
    const getAllReq = store.getAll();
    getAllReq.onsuccess = () => {
      const records: { key: string; areaId?: string }[] = getAllReq.result || [];
      const toDelete = records.filter(r => r.areaId === areaId);
      toDelete.forEach(r => store.delete(r.key));
      tx.oncomplete = () => resolve(toDelete.length);
      tx.onerror    = () => reject(tx.error);
    };
  });
}

// Funzione principale: scarica tutte le tile per l'area
// layer: 'satellite' | 'street' | 'topo' | 'opentopomap'
// La chiave in IndexedDB è "{layer}/{z}/{x}/{y}" per evitare collisioni tra layer
export async function precacheTiles(
  lat: number,
  lon: number,
  radiusKm: number = 5,
  zoomMin: number = 10,
  zoomMax: number = 15,
  onProgress?: (p: TileCacheProgress) => void,
  signal?: AbortSignal,
  areaId?: string,
  layer: string = 'satellite'
): Promise<TileCacheProgress> {

  const db = await openTileDB();
  const urlTemplate = TILE_LAYER_URLS[layer] ?? TILE_LAYER_URLS.satellite;

  // Raccoglie tutte le tile da scaricare
  const allTiles: { z: number; x: number; y: number }[] = [];
  for (let z = zoomMin; z <= zoomMax; z++) {
    allTiles.push(...getTilesForArea(lat, lon, radiusKm, z));
  }

  const total = allTiles.length;
  let downloaded = 0;
  let failed = 0;

  const BATCH = 4;
  const TILE_TIMEOUT_MS = 10000;

  const fetchTile = async (url: string, extSignal?: AbortSignal): Promise<Response> => {
    const ctrl = new AbortController();
    const tid  = setTimeout(() => ctrl.abort(), TILE_TIMEOUT_MS);
    const onExt = () => ctrl.abort();
    extSignal?.addEventListener('abort', onExt);
    try {
      return await fetch(url, { signal: ctrl.signal });
    } finally {
      clearTimeout(tid);
      extSignal?.removeEventListener('abort', onExt);
    }
  };

  for (let i = 0; i < allTiles.length; i += BATCH) {
    if (signal?.aborted) break;

    const batch = allTiles.slice(i, i + BATCH);
    await Promise.all(batch.map(async ({ z, x, y }) => {
      const key = `${layer}/${z}/${x}/${y}`;
      const url = urlTemplate
        .replace('{z}', String(z))
        .replace('{x}', String(x))
        .replace('{y}', String(y));
      try {
        const resp = await fetchTile(url, signal);
        if (resp.ok) {
          const blob = await resp.blob();
          await saveTile(db, key, blob, areaId);
          downloaded++;
        } else {
          failed++;
        }
      } catch {
        failed++;
      }
    }));

    onProgress?.({
      total,
      downloaded,
      failed,
      percent: Math.round((downloaded + failed) / total * 100),
      done: false,
    });
  }

  const result: TileCacheProgress = { total, downloaded, failed, percent: 100, done: true };
  onProgress?.(result);
  return result;
}
